import { Fragment, useState } from "react";
import { Button, Dropdown, Modal, Form } from "react-bootstrap";

import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchUserGroup, fetchPossibleTeamReviewers, addReviewer, unassignReviewer } from "../../services/PartnerService";
import { toast } from "react-toastify";
import { hasPermission } from "../../utils/auth/accessRules";
import { PERMISSION_SLUGS } from "../../utils/auth/permissionConstants";

const statusBadgeClass = (status) => {
  const value = status?.toLowerCase();
  if (value === 'active')    return 'badge-success';
  if (value === 'pending')   return 'badge-warning';
  if (value === 'inactive')  return 'badge-danger';
  if (value === 'suspended') return 'badge-light';
  return 'badge-secondary';
};

const StatusBadge = ({ status }) => (
  <span className={`badge badge-rounded ${statusBadgeClass(status)}`}>{status}</span>
);

const TeamDetails = () => {
  const { teamId: paramTeamId, partnerId: paramPartnerId } = useParams();
  const queryClient = useQueryClient();
  const authUser = useSelector((state) => state.auth.authUser);
  const canManage = hasPermission(authUser, PERMISSION_SLUGS.MANAGE_TEAMS);

  const { data: userGroupData, isPending: teamLoading, isError: teamError } = useQuery({
    queryKey: ['userGroup', paramPartnerId, paramTeamId],
    queryFn: () => fetchUserGroup(paramPartnerId, paramTeamId),
    enabled: !!paramPartnerId && !!paramTeamId,
    staleTime: 5 * 60 * 1000,
  });
  const team = userGroupData?.data?.data ?? null;

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedReviewerId, setSelectedReviewerId] = useState('');
  const [approvalLimit, setApprovalLimit] = useState('');
  const [reviewerSearch, setReviewerSearch] = useState('');

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [reviewerToRemove, setReviewerToRemove] = useState(null);

  const { data: possibleReviewersData, isLoading: possibleReviewersLoading } = useQuery({
    queryKey: ['possibleTeamReviewers', paramPartnerId],
    queryFn: () => fetchPossibleTeamReviewers(paramPartnerId),
    enabled: showAddModal && !!paramPartnerId,
    staleTime: 5 * 60 * 1000,
  });
  const possibleReviewers = possibleReviewersData?.data?.data ?? [];

  const addReviewerMutation = useMutation({
    mutationFn: (data) => addReviewer(paramPartnerId, paramTeamId, data),
    onSuccess: () => {
      setShowAddModal(false);
      setSelectedReviewerId('');
      setApprovalLimit('');
      queryClient.invalidateQueries({ queryKey: ['userGroup', paramPartnerId, paramTeamId] });
      toast.success("Reviewer added successfully.");
    },
  });

  const unassignReviewerMutation = useMutation({
    mutationFn: (reviewerId) => unassignReviewer(paramPartnerId, paramTeamId, reviewerId),
    onSuccess: () => {
      setShowRemoveModal(false);
      setReviewerToRemove(null);
      queryClient.invalidateQueries({ queryKey: ['userGroup', paramPartnerId, paramTeamId] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message ?? 'Failed to remove reviewer. Please try again.');
    },
  });

  const handleAddReviewer = () => {
    const data = { reviewer_id: selectedReviewerId };
    if (approvalLimit !== '') {
      data.user_transaction_limit_extension_max_amount = parseInt(approvalLimit, 10);
    }
    addReviewerMutation.mutate(data);
  };

  const handleCloseAddModal = () => {
    if (addReviewerMutation.isPending) return;
    setShowAddModal(false);
    setSelectedReviewerId('');
    setApprovalLimit('');
    setReviewerSearch('');
    addReviewerMutation.reset();
  };

  const handleCloseRemoveModal = () => {
    if (unassignReviewerMutation.isPending) return;
    setShowRemoveModal(false);
    setReviewerToRemove(null);
    unassignReviewerMutation.reset();
  };

  const handleRemoveReviewer = (reviewerId) => {
    unassignReviewerMutation.mutate(reviewerId);
  };

  const renderStateBlock = (colSpan) => {
    if (teamLoading) {
      return (
        <tr>
          <td colSpan={colSpan} className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </td>
        </tr>
      );
    }
    if (teamError) {
      return (
        <tr>
          <td colSpan={colSpan} className="text-center text-danger py-4">
            Failed to load team details. Please try again.
          </td>
        </tr>
      );
    }
    return null;
  };

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card overflow-hidden">
            <div className="text-center p-3 bg-primary">
              <h3 className="mt-3 mb-1 text-white">{team?.name}</h3>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Reviewers</h4>
              {canManage && (
                <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                  <i className="fa fa-plus me-1" aria-hidden="true" />
                  Add Reviewer
                </Button>
              )}
            </div>
            <div className="card-body p-0">
              <div className="table-responsive d-none d-lg-block px-3 pt-2">
                <table className="table verticle-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                      {canManage && <th scope="col" className="text-end">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {teamLoading || teamError ? (
                      renderStateBlock(canManage ? 4 : 3)
                    ) : team?.reviewers?.length > 0 ? (
                      team.reviewers.map((reviewer) => (
                        <tr key={reviewer.id}>
                          <td style={{ overflowWrap: 'anywhere' }}>{reviewer.full_name}</td>
                          <td>{reviewer.role}</td>
                          <td><StatusBadge status={reviewer.status} /></td>
                          {canManage && (
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setReviewerToRemove(reviewer);
                                  setShowRemoveModal(true);
                                }}
                                aria-label={`Remove ${reviewer.full_name}`}
                              >
                                <i className="fa fa-trash" aria-hidden="true" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={canManage ? 4 : 3} className="text-center text-muted py-4">
                          No reviewers
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <ul className="list-group list-group-flush d-block d-lg-none">
                {teamLoading || teamError ? (
                  <li className="list-group-item text-center text-muted py-4">
                    {teamError ? 'Failed to load reviewers.' : 'Loading...'}
                  </li>
                ) : team?.reviewers?.length > 0 ? (
                  team.reviewers.map((reviewer) => (
                    <li
                      key={reviewer.id}
                      className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-nowrap py-3"
                    >
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                          <span className="fw-semibold text-body" style={{ overflowWrap: 'anywhere' }}>
                            {reviewer.full_name}
                          </span>
                          <StatusBadge status={reviewer.status} />
                        </div>
                        <div className="small text-muted">{reviewer.role}</div>
                      </div>
                      {canManage && (
                        <div className="flex-shrink-0">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              setReviewerToRemove(reviewer);
                              setShowRemoveModal(true);
                            }}
                            aria-label={`Remove ${reviewer.full_name}`}
                          >
                            <i className="fa fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-center text-muted py-4">No reviewers</li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center gap-2">
              <h4 className="card-title mb-0">Members</h4>
              {canManage && team?.id && (
                <Link
                  to={`/users?team=${team.id}`}
                  className="btn btn-sm btn-outline-primary"
                >
                  <i className="fa fa-users me-1" aria-hidden="true" />
                  Manage members
                </Link>
              )}
            </div>
            <div className="card-body p-0">
              <div className="table-responsive d-none d-lg-block px-3 pt-2">
                <table className="table verticle-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamLoading || teamError ? (
                      renderStateBlock(3)
                    ) : team?.members?.length > 0 ? (
                      team.members.map((member) => (
                        <tr key={member.id}>
                          <td style={{ overflowWrap: 'anywhere' }}>{member.full_name}</td>
                          <td>{member.role}</td>
                          <td><StatusBadge status={member.status} /></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">
                          No members
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <ul className="list-group list-group-flush d-block d-lg-none">
                {teamLoading || teamError ? (
                  <li className="list-group-item text-center text-muted py-4">
                    {teamError ? 'Failed to load members.' : 'Loading...'}
                  </li>
                ) : team?.members?.length > 0 ? (
                  team.members.map((member) => (
                    <li
                      key={member.id}
                      className="list-group-item d-flex justify-content-between align-items-start gap-3 flex-nowrap py-3"
                    >
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                          <span className="fw-semibold text-body" style={{ overflowWrap: 'anywhere' }}>
                            {member.full_name}
                          </span>
                          <StatusBadge status={member.status} />
                        </div>
                        <div className="small text-muted">{member.role}</div>
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item text-center text-muted py-4">No members</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showAddModal} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Reviewer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {possibleReviewersLoading ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : possibleReviewers.length === 0 ? (
            <p className="text-muted mb-0">No eligible reviewers available for this team.</p>
          ) : (
            <Form>
              <div className="mb-3">
                <label className="form-label d-block">Reviewer</label>
                {(() => {
                  const selected = possibleReviewers.find(
                    (user) => String(user.id) === String(selectedReviewerId)
                  );
                  const currentLabel = selected
                    ? `${selected.full_name}, ${selected.role}`
                    : 'Select a reviewer...';
                  const needle = reviewerSearch.trim().toLowerCase();
                  const filteredReviewers = needle
                    ? possibleReviewers.filter((user) =>
                        `${user.full_name} ${user.role}`.toLowerCase().includes(needle)
                      )
                    : possibleReviewers;
                  return (
                    <Dropdown
                      className="w-100"
                      autoClose="outside"
                      onToggle={(nextOpen) => { if (!nextOpen) setReviewerSearch(''); }}
                    >
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        className="d-inline-flex align-items-center w-100"
                        disabled={addReviewerMutation.isPending}
                      >
                        <span className="flex-grow-1 text-start">{currentLabel}</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100 p-0">
                        <div className="p-2 border-bottom">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search"
                            value={reviewerSearch}
                            onChange={(e) => setReviewerSearch(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {filteredReviewers.length === 0 ? (
                            <div className="px-3 py-2 text-muted small">No reviewers match.</div>
                          ) : filteredReviewers.map((user) => (
                            <Dropdown.Item
                              key={user.id}
                              active={String(selectedReviewerId) === String(user.id)}
                              onClick={() => setSelectedReviewerId(user.id)}
                            >
                              {user.full_name}, {user.role}
                            </Dropdown.Item>
                          ))}
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  );
                })()}
              </div>
              <Form.Group>
                <Form.Label>Approval limit (optional)</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">KES</span>
                  <Form.Control
                    type="number"
                    min="1"
                    placeholder="No limit"
                    value={approvalLimit}
                    onChange={(e) => setApprovalLimit(e.target.value)}
                    disabled={addReviewerMutation.isPending}
                  />
                </div>
                <Form.Text className="text-muted">
                  Leave blank to allow this reviewer to approve transactions of any amount.
                </Form.Text>
              </Form.Group>
              {addReviewerMutation.isError && (
                <p className="text-danger mt-3 mb-0">
                  {addReviewerMutation.error?.response?.data?.message ?? 'Something went wrong. Please try again.'}
                </p>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddModal} disabled={addReviewerMutation.isPending}>
            Cancel
          </Button>
          {possibleReviewers.length > 0 && (
            <Button
              variant="primary"
              onClick={handleAddReviewer}
              disabled={!selectedReviewerId || addReviewerMutation.isPending}
            >
              {addReviewerMutation.isPending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Adding...
                </>
              ) : (
                'Add Reviewer'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showRemoveModal} onHide={handleCloseRemoveModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <strong>{reviewerToRemove?.full_name}</strong> from the reviewers list?
          {unassignReviewerMutation.isError && (
            <p className="text-danger mt-2 mb-0">
              {unassignReviewerMutation.error?.response?.data?.message ?? 'Something went wrong. Please try again.'}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRemoveModal} disabled={unassignReviewerMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleRemoveReviewer(reviewerToRemove.team_reviewer_id)}
            disabled={unassignReviewerMutation.isPending}
          >
            {unassignReviewerMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Removing...
              </>
            ) : (
              'Remove'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default TeamDetails;
